// src/pages/PartyBillsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaSyncAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import { API_ENDPOINTS, API_BASE_URL } from "../config/constants";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#fff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const fmtINR = formatStoredMoney;

const PartyBillsPage = () => {
  const [userId, setUserId] = useState(null);
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");
  const [monthDate, setMonthDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const reportRef = useRef(null);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchParties();
  }, [userId]);

  useEffect(() => {
    if (userId && selectedParty) fetchBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParty, monthDate, userId]);

  const fetchParties = async () => {
    try {
      const res = await fetch(
        API_ENDPOINTS.LIST_PARTY_FOR_ORDERS,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId }),
        }
      );
      const json = await res.json();
      if (!res.ok || json?.success === false)
        throw new Error(json?.message || "Failed to load parties");
      setParties(json.list || []);
      if (json.list?.length) setSelectedParty(json.list[0].id);
    } catch (e) {
      toast.error(e.message || "Unable to load parties");
    } finally {
      setLoading(false);
    }
  };

  const fetchBills = async (opts = {}) => {
    if (!selectedParty) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const monthStr = `${monthDate.getFullYear()}-${String(
        monthDate.getMonth() + 1
      ).padStart(2, "0")}`;
      const res = await fetch(`${API_BASE_URL}/party-bills`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          partyid: selectedParty,
          month: monthStr,
        }),
      });
      const json = await res.json();
      if (!res.ok || json?.success === false) {
        setBills([]);
        if (json?.message) toast.info(json.message);
      } else {
        setBills(json.data || []);
      }
    } catch (e) {
      toast.error(e.message || "Unable to load bills");
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const filteredParties = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parties;
    return parties.filter(
      (p) =>
        p.partyname?.toLowerCase().includes(q) ||
        String(p.partycontactpersonmobile || "").includes(q)
    );
  }, [parties, search]);

  const totalAmount = useMemo(
    () => bills.reduce((sum, b) => sum + Number(b.total_amount || 0), 0),
    [bills]
  );

  const partyName = useMemo(
    () => parties.find((p) => p.id === selectedParty)?.partyname || "",
    [parties, selectedParty]
  );

  const goMonth = (delta) => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() + delta);
    const now = new Date();
    d.setDate(1);
    if (d > now) return;
    setMonthDate(d);
  };

  if (loading && !parties.length) {
    return (
      <Center>
        <div className="spinner" />
        <div>Loading...</div>
      </Center>
    );
  }

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Party Wise Bills</Title>
          <Sub>Pick a party and month to see bills.</Sub>
        </div>
        <PrimaryBtn onClick={() => fetchBills({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </Top>

      <Grid>
        <Card>
          <CardHead>
            <CardTitle>Parties</CardTitle>
            <SearchBox>
              <FaSearch />
              <input
                placeholder="Search by name or mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBox>
          </CardHead>
          <PartyList>
            {filteredParties.map((p) => (
              <PartyRow
                key={p.id}
                $active={selectedParty === p.id}
                onClick={() => setSelectedParty(p.id)}
              >
                <div className="left">
                  <Avatar>{(p.partyname || "?").charAt(0).toUpperCase()}</Avatar>
                  <div>
                    <div className="name">{p.partyname}</div>
                    <div className="sub">{p.partycontactpersonmobile || "-"}</div>
                  </div>
                </div>
                <Arrow>›</Arrow>
              </PartyRow>
            ))}
            {!filteredParties.length && <Center>No parties found.</Center>}
          </PartyList>
        </Card>

        <Card ref={reportRef}>
          <CardHead>
            <div>
              <CardTitle>{partyName || "Select a party"}</CardTitle>
              <CardSub>
                Month:{" "}
                {monthDate.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })}
              </CardSub>
            </div>
            <MonthRow>
              <NavBtn onClick={() => goMonth(-1)}>
                <FaChevronLeft />
              </NavBtn>
              <NavBtn onClick={() => goMonth(1)}>
                <FaChevronRight />
              </NavBtn>
            </MonthRow>
          </CardHead>

          <SummaryRow>
            <SummaryCard>
              <small>Total Bills</small>
              <strong>{bills.length}</strong>
            </SummaryCard>
            <SummaryCard>
              <small>Grand Total</small>
              <strong>{fmtINR(totalAmount)}</strong>
            </SummaryCard>
          </SummaryRow>

          {loading ? (
            <Center>Loading bills…</Center>
          ) : bills.length === 0 ? (
            <Center>No bills for this month.</Center>
          ) : (
            <BillsList>
              {bills.map((b) => (
                <BillCard key={b.bill_id}>
                  <BillTop>
                    <div>
                      <BillTitle>Bill #{b.bill_id}</BillTitle>
                      <BillMeta>
                        {new Date(b.date).toLocaleString()} · Qty {b.total_qty}
                      </BillMeta>
                    </div>
                    <AmountBadge>{fmtINR(b.total_amount)}</AmountBadge>
                  </BillTop>
                  {Array.isArray(b.item_summary) && b.item_summary.length > 0 && (
                    <ItemsTable>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th style={{ width: 80 }}>Qty</th>
                          <th style={{ width: 120 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b.item_summary.map((i, idx) => (
                          <tr key={idx}>
                            <td>{i.item_name}</td>
                            <td>{i.total_quantity}</td>
                            <td>{fmtINR(i.total_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </ItemsTable>
                  )}
                </BillCard>
              ))}
            </BillsList>
          )}
          <Actions style={{ marginTop: 10 }}>
            <PrimaryBtn
              onClick={() => {
                const headers = ["Bill ID", "Date", "Qty", "Amount"];
                const rows = bills.map((b) => [
                  b.bill_id,
                  b.date,
                  b.total_qty,
                  b.total_amount,
                ]);
                downloadExcel(
                  `party_bills_${partyName || "party"}_${monthDate.getFullYear()}_${
                    monthDate.getMonth() + 1
                  }.xlsx`,
                  headers,
                  rows
                );
              }}
            >
              Excel
            </PrimaryBtn>
            <PrimaryBtn
              onClick={() =>
                downloadPDF(
                  reportRef.current,
                  `party_bills_${partyName || "party"}_${monthDate.getFullYear()}_${
                    monthDate.getMonth() + 1
                  }.pdf`
                )
              }
            >
              <FaSyncAlt /> PDF
            </PrimaryBtn>
            <PrimaryBtn
              onClick={() =>
                printThermal(
                  reportRef.current,
                  `party_bills_${partyName || "party"}_${monthDate.getFullYear()}_${
                    monthDate.getMonth() + 1
                  }`
                )
              }
            >
              Thermal Print
            </PrimaryBtn>
            <PrimaryBtn
              onClick={() => {
                const headers = ["Bill ID", "Date", "Qty", "Amount"];
                const rows = bills.map((b) => [
                  b.bill_id,
                  b.date,
                  b.total_qty,
                  b.total_amount,
                ]);
                downloadCSV(
                  `party_bills_${partyName || "party"}_${monthDate.getFullYear()}_${
                    monthDate.getMonth() + 1
                  }.csv`,
                  headers,
                  rows
                );
              }}
            >
              <FaSyncAlt /> CSV
            </PrimaryBtn>
          </Actions>
        </Card>
      </Grid>
    </Page>
  );
};

export default PartyBillsPage;

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
  font-family: Inter, system-ui, Segoe UI, Roboto, "Helvetica Neue", Arial;
`;
const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 12px;
  align-items: start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;
const CardTitle = styled.h3`
  margin: 0;
  color: ${COLOR.text};
`;
const CardSub = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
`;
const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;
const PartyList = styled.div`
  display: grid;
  gap: 8px;
  max-height: 70vh;
  overflow-y: auto;
`;
const PartyRow = styled.button`
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px;
  background: ${({ $active }) => ($active ? "#eef2ff" : "#fff")};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  .left {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .name {
    font-weight: 800;
    color: ${COLOR.text};
  }
  .sub {
    color: ${COLOR.muted};
    font-size: 12px;
  }
`;
const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #eef2ff;
  color: ${COLOR.primary};
  display: grid;
  place-items: center;
  font-weight: 900;
`;
const Arrow = styled.div`
  color: ${COLOR.muted};
  font-weight: 900;
`;

const MonthRow = styled.div`
  display: inline-flex;
  gap: 6px;
`;
const NavBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid ${COLOR.line};
  background: #f9f9f9;
  cursor: pointer;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
  margin: 10px 0;
`;
const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px;
  small {
    color: ${COLOR.muted};
    font-weight: 700;
    text-transform: uppercase;
  }
  strong {
    display: block;
    margin-top: 4px;
    font-size: 1.05rem;
  }
`;

const BillsList = styled.div`
  display: grid;
  gap: 10px;
`;
const BillCard = styled.div`
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 12px;
  background: #f9fafb;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
`;
const BillTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;
const BillTitle = styled.div`
  font-weight: 900;
  color: ${COLOR.text};
`;
const BillMeta = styled.div`
  color: ${COLOR.muted};
  font-size: 12px;
`;
const AmountBadge = styled.span`
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  color: ${COLOR.text};
  padding: 6px 10px;
  border-radius: 10px;
  font-weight: 800;
`;
const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
  th,
  td {
    padding: 8px;
    border-bottom: 1px solid ${COLOR.line};
    text-align: left;
  }
  thead {
    background: #eef2ff;
  }
`;

const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 12px;
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: ${COLOR.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
